import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, orderBy, onSnapshot, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { refundVideoCallUsage } from '../../utils/videoCallDatabase';
import { isVideoCallAvailableNow } from '../../utils/videoCallUtils';
import VideoCallUsageBadge from './VideoCallUsageBadge';
import { VideoCallIcon } from '../Layout/ProfileMenuDropdown';

const MyVideoCallRequests = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, scheduled, active, completed, rejected, cancelled
  const [storeNames, setStoreNames] = useState({}); // Cache store names
  const [availabilityStatus, setAvailabilityStatus] = useState({}); // { requestId: { available: boolean, reason: string } }

  useEffect(() => {
    if (currentUser?.uid) {
      loadRequests();
      const unsubscribe = subscribeToRequests();
      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [currentUser, filter]);

  // Check availability for approved/scheduled requests
  useEffect(() => {
    if (requests.length > 0) {
      checkAvailabilityForRequests();
    }
  }, [requests]);

  const checkAvailabilityForRequests = async () => {
    const statusMap = {};
    
    for (const request of requests) {
      if (request.status === 'approved' || request.status === 'scheduled') {
        try {
          const result = await isVideoCallAvailableNow(request.storeId, request.productId);
          statusMap[request.id] = {
            available: result.available,
            reason: result.reason || null,
            nextAvailable: result.nextAvailable || null
          };
        } catch (error) {
          console.error('Error checking availability for request:', request.id, error);
          statusMap[request.id] = {
            available: false,
            reason: 'Error checking availability'
          };
        }
      }
    }
    
    setAvailabilityStatus(statusMap);
  };

  const loadRequests = async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('🔍 Loading my video call requests for buyerId:', currentUser.uid);
      
      const requestsRef = collection(db, 'videoCallRequests');
      let q = query(
        requestsRef,
        where('buyerId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );

      try {
        const snapshot = await getDocs(q);
        console.log('📊 Found', snapshot.size, 'my video call requests for buyerId:', currentUser.uid);
        
        // Debug: Check all requests in collection (without filter)
        try {
          const allRequestsRef = collection(db, 'videoCallRequests');
          const allSnapshot = await getDocs(allRequestsRef);
          console.log('🔍 DEBUG: Total requests in collection:', allSnapshot.size);
          console.log('🔍 DEBUG: Looking for buyerId:', currentUser.uid);
          allSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const requestBuyerId = data.buyerId;
            const matches = requestBuyerId === currentUser.uid;
            console.log('🔍 All Request:', {
              id: doc.id,
              buyerId: requestBuyerId,
              matches: matches ? '✅ MATCH' : '❌ NO MATCH',
              storeId: data.storeId,
              status: data.status,
              productName: data.productName,
              createdAt: data.createdAt
            });
          });
        } catch (debugError) {
          console.error('Error checking all requests:', debugError);
        }
        
        const requestsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data
          };
        });

        setRequests(requestsData);
        
        // Load store names
        loadStoreNames(requestsData);
      } catch (indexError) {
        // If index is missing, try without orderBy
        if (indexError.code === 'failed-precondition') {
          console.warn('⚠️ Index missing, trying without orderBy...');
          const fallbackQ = query(
            requestsRef,
            where('buyerId', '==', currentUser.uid)
          );
          
          const snapshot = await getDocs(fallbackQ);
          console.log('📊 Found', snapshot.size, 'my video call requests (without orderBy)');
          
          const requestsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Sort manually
          requestsData.sort((a, b) => {
            const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return bTime - aTime;
          });
          
          setRequests(requestsData);
          console.warn('💡 Please create Firestore index: videoCallRequests (buyerId, createdAt)');
          
          // Load store names
          loadStoreNames(requestsData);
        } else {
          throw indexError;
        }
      }
    } catch (error) {
      console.error('❌ Error loading my video call requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStoreNames = async (requestsData) => {
    const storeIds = [...new Set(requestsData.map(r => r.storeId).filter(Boolean))];
    const names = {};
    
    await Promise.all(storeIds.map(async (storeId) => {
      try {
        const storeRef = doc(db, 'stores', storeId);
        const storeSnap = await getDoc(storeRef);
        if (storeSnap.exists()) {
          names[storeId] = storeSnap.data().name || 'N/A';
        }
      } catch (error) {
        console.error('Error loading store name:', error);
        names[storeId] = 'N/A';
      }
    }));
    
    setStoreNames(names);
  };

  const subscribeToRequests = () => {
    if (!currentUser?.uid) {
      return () => {};
    }

    const requestsRef = collection(db, 'videoCallRequests');
    const q = query(
      requestsRef,
      where('buyerId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, 
      (snapshot) => {
        console.log('🔄 Real-time update: Found', snapshot.size, 'my requests');
        const requestsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRequests(requestsData);
        
        // Load store names
        loadStoreNames(requestsData);
      },
      (error) => {
        console.error('❌ Error in real-time subscription:', error);
        if (error.code === 'failed-precondition') {
          console.warn('⚠️ Firestore index may be missing. Please create index for: videoCallRequests (buyerId, createdAt)');
        }
      }
    );
  };

  const filteredRequests = filter === 'all' 
    ? requests 
    : requests.filter(r => r.status === filter);

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      scheduled: 'bg-green-100 text-green-800',
      active: 'bg-purple-100 text-purple-800',
      completed: 'bg-gray-100 text-gray-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    const labels = {
      pending: t('videoCall.pending'),
      approved: t('videoCall.approved'),
      scheduled: t('videoCall.scheduled'),
      active: t('videoCall.active'),
      completed: t('videoCall.completed'),
      rejected: t('videoCall.rejected'),
      cancelled: t('videoCall.cancelled')
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badges[status] || badges.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <VideoCallIcon className="w-6 h-6 text-purple-600" />
          {t('videoCall.myRequests')}
        </h2>
        <div className="flex items-center gap-4">
          <VideoCallUsageBadge showFullInfo={false} />
          <div className="text-sm text-gray-600">
            {t('common.all')}: {requests.length} | 
            {t('videoCall.pending')}: {requests.filter(r => r.status === 'pending').length}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {['all', 'pending', 'approved', 'scheduled', 'active', 'completed', 'rejected', 'cancelled'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === status
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status === 'all' ? t('common.all') : 
             status === 'pending' ? t('videoCall.pending') :
             status === 'approved' ? t('videoCall.approved') :
             status === 'scheduled' ? t('videoCall.scheduled') :
             status === 'active' ? t('videoCall.active') :
             status === 'completed' ? t('videoCall.completed') :
             status === 'rejected' ? t('videoCall.rejected') :
             status === 'cancelled' ? t('videoCall.cancelled') : status}
          </button>
        ))}
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">{t('videoCall.noRequests')}</p>
            <p className="text-sm mt-2">
              {filter === 'all' 
                ? t('videoCall.noRequestsSent') 
                : t('videoCall.noRequestsWithStatus', { status: filter })}
            </p>
          </div>
        ) : (
          filteredRequests.map(request => (
            <div
              key={request.id}
              className="border rounded-lg p-4 hover:shadow-md transition-shadow border-gray-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusBadge(request.status)}
                    <span className="font-semibold text-gray-800">
                      📦 {request.productName || 'N/A'}
                    </span>
                  </div>
                  
                  <div className="ml-0 space-y-1">
                    <p className="text-sm text-gray-600">
                      🏪 ร้าน: {storeNames[request.storeId] || request.storeName || request.storeId || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600">
                      ⏰ ขอเวลา: {formatTime(request.requestedTime)}
                    </p>
                    {request.scheduledTime && (
                      <p className="text-sm text-green-600">
                        📅 จองเวลา: {formatTime(request.scheduledTime)}
                      </p>
                    )}
                    {(request.status === 'approved' || request.status === 'scheduled') && (
                      <p className="text-xs text-blue-600">
                        💳 {t('videoCall.usageDeducted')}
                      </p>
                    )}
                    {request.rejectionReason && (
                      <p className="text-sm text-red-600">
                        ❌ เหตุผล: {request.rejectionReason}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      📅 ส่งเมื่อ: {formatTime(request.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2 flex-col">
                  {request.status === 'approved' || request.status === 'scheduled' ? (
                    <>
                      {(() => {
                        const availStatus = availabilityStatus[request.id];
                        const isAvailable = availStatus?.available === true;
                        const scheduledTime = request.scheduledTime?.toDate ? request.scheduledTime.toDate() : new Date(request.scheduledTime);
                        const now = new Date();
                        const isScheduledTime = scheduledTime && now >= scheduledTime;
                        const canStart = isAvailable || isScheduledTime; // ร้านว่าง หรือ ถึงเวลาแล้ว
                        
                        return (
                          <>
                            <button
                              onClick={() => {
                                if (canStart) {
                                  navigate(`/marketplace/video-call/${request.chatId || request.id}`);
                                } else {
                                  // Show reason why can't start
                                  const reason = availStatus?.reason || 'ร้านไม่ว่างตอนนี้';
                                  alert(`ไม่สามารถเริ่ม Video Call ได้: ${reason}`);
                                }
                              }}
                              disabled={!canStart}
                              className={`px-3 py-1 text-sm text-white rounded ${
                                canStart
                                  ? 'bg-purple-500 hover:bg-purple-600'
                                  : 'bg-gray-400 cursor-not-allowed'
                              }`}
                            >
                              <VideoCallIcon className="w-4 h-4" />
                              <span>{canStart ? 'เริ่ม Call' : 'รอ...'}</span>
                            </button>
                            {!canStart && availStatus && (
                              <p className="text-xs text-gray-500 mt-1">
                                {availStatus.reason === 'Not in scheduled time' 
                                  ? `⏰ รอถึงเวลา: ${formatTime(scheduledTime)}`
                                  : availStatus.reason === 'No slots available'
                                  ? '🔴 ร้านไม่ว่างตอนนี้'
                                  : `ℹ️ ${availStatus.reason || 'รอให้ร้านว่าง'}`}
                              </p>
                            )}
                            {canStart && !isScheduledTime && (
                              <p className="text-xs text-green-600 mt-1">
                                ✅ ร้านว่างแล้ว (เริ่มก่อนเวลาได้)
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </>
                  ) : null}
                  
                  {request.status === 'pending' && (
                    <button
                      onClick={async () => {
                        if (window.confirm(t('videoCall.confirmCancel'))) {
                          try {
                            const requestRef = doc(db, 'videoCallRequests', request.id);
                            await updateDoc(requestRef, {
                              status: 'cancelled',
                              cancelledAt: serverTimestamp(),
                              updatedAt: serverTimestamp()
                            });
                            alert(t('videoCall.cancelSuccess'));
                          } catch (error) {
                            console.error('Error cancelling request:', error);
                            alert('เกิดข้อผิดพลาด: ' + error.message);
                          }
                        }
                      }}
                      className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      {t('videoCall.cancel')}
                    </button>
                  )}
                  {(request.status === 'approved' || request.status === 'scheduled') && (
                    <button
                      onClick={async () => {
                        if (window.confirm(t('videoCall.confirmCancelApproved'))) {
                          try {
                            // Refund usage
                            await refundVideoCallUsage(currentUser.uid, request.id);
                            
                            // Update request status
                            const requestRef = doc(db, 'videoCallRequests', request.id);
                            await updateDoc(requestRef, {
                              status: 'cancelled',
                              cancelledAt: serverTimestamp(),
                              updatedAt: serverTimestamp()
                            });
                            
                            alert(t('videoCall.cancelSuccessRefunded'));
                          } catch (error) {
                            console.error('Error cancelling approved request:', error);
                            alert('เกิดข้อผิดพลาด: ' + error.message);
                          }
                        }
                      }}
                      className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      {t('videoCall.cancel')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MyVideoCallRequests;

